import { Alert, Button, Icons } from '@dbrain/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { api } from '../../api/client.js';
import { keys, useModels, useProviders, useRoutes, type ModelOption } from '../../api/hooks.js';
import { CapacityDaySelect, CapacityPanel, type CapacityDay } from '../../components/admin/Capacity.js';
import {
  AddProviderDialog,
  inheritedLabel,
  modelName,
  modelUsage,
  ProviderCard,
  ProviderList,
  TaskModels,
  taskStates,
  type ModelRow,
  type ProviderDraft,
  type ProviderEndpoints,
  type ProviderView,
} from '../../components/admin/providers.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { useCapabilities } from '../../experience.js';
import { AdminLayout, SubTabs } from './AdminLayout.js';

/** The endpoints of an organization's or a member's own provider (06 §8). */
export const providerEndpoints = (id: string): ProviderEndpoints => ({
  test: `/api/providers/${id}/test`,
  refresh: `/api/providers/${id}/refresh`,
  update: `/api/providers/${id}`,
  remove: `/api/providers/${id}`,
  model: (modelId) => `/api/models/${modelId}`,
});

/** The installation's providers with their models and its routes (the platform's in bireysel). */
export interface InstallationView {
  providers: (ProviderView & { keyFromEnvironment: boolean; capacityManaged: boolean; seeded: boolean; logOptOut: boolean; models: ModelRow[] })[];
  routes: { callType: string; modelRef: string }[];
  /** 16c.1, bireysel: the packages listing each model ("Ücretsiz · Pro") */
  plans?: Record<string, ('free' | 'pro')[]>;
  /** 16c.1, kurumsal: the company's teams and their people ("Kimler kullanabilir") */
  teams?: { id: string; name: string; members: number }[];
}

/** The endpoints of the installation's provider, under the platform operator's or the kurumsal super admin's prefix. */
export const installationEndpoints = (prefix: string, id: string): ProviderEndpoints => ({
  test: `${prefix}/providers/${id}/test`,
  refresh: `${prefix}/providers/${id}/refresh`,
  update: `${prefix}/providers/${id}`,
  remove: `${prefix}/providers/${id}`,
  model: (modelId) => `${prefix}/models/${modelId}`,
});

/** The body of a new installation-level provider (the platform's schema has no scope and always an address). */
export const installationDraft = (draft: ProviderDraft) => ({
  name: draft.name,
  kind: draft.kind,
  baseUrl: draft.baseUrl ?? '',
  ...(draft.apiKey ? { apiKey: draft.apiKey } : {}),
  ...(draft.capacityManaged !== undefined ? { capacityManaged: draft.capacityManaged } : {}),
  ...(draft.logOptOut !== undefined ? { logOptOut: draft.logOptOut } : {}),
});

/** A company's or a member's own provider: the draft to try, and the one to add. */
export const ownDraftTest = (scope: 'tenant' | 'user', draft: ProviderDraft) => ({
  scope,
  kind: draft.kind,
  ...(draft.baseUrl ? { baseUrl: draft.baseUrl } : {}),
  ...(draft.apiKey ? { apiKey: draft.apiKey } : {}),
  ...(draft.modelId ? { modelId: draft.modelId } : {}),
});
export const ownDraft = (scope: 'tenant' | 'user', draft: ProviderDraft) => ({ ...ownDraftTest(scope, draft), name: draft.name });

const routeMap = (routes: { callType: string; modelRef: string }[]) => Object.fromEntries(routes.map((r) => [r.callType, r.modelRef]));

/**
 * Kurumsal Yönetim → Modeller (D-135, KurumsalModeller): the installation's providers and the model per task,
 * which is what everyone uses; no company level and no "Platform" label. Providers a company added before
 * Phase 13g are listed too and keep working.
 */
function InstallationModels({ adding, onAdding }: { adding: boolean; onAdding: (open: boolean) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const prefix = '/api/admin/models';
  const view = useQuery({ queryKey: keys.adminModels, queryFn: () => api<InstallationView>('GET', prefix) });
  const providers = useProviders();
  const models = useModels();
  const routes = useRoutes();
  const refresh = () => {
    for (const key of [keys.adminModels, keys.providers, keys.models, keys.routes]) void queryClient.invalidateQueries({ queryKey: key });
  };
  const company = (providers.data ?? []).filter((p) => p.scope === 'tenant');
  const companyModels = (models.data?.models ?? []).filter((m) => m.scope === 'tenant');
  const installation = view.data?.providers ?? [];
  // a company's own choice from before still wins until a choice is made here (D-135)
  const own = { ...routeMap(view.data?.routes ?? []), ...routeMap((routes.data ?? []).filter((r) => r.scope === 'tenant')) };
  const states = taskStates(own);
  const usage = modelUsage(states);
  const choosable = [
    ...installation.flatMap((p) => p.models.map((m) => ({ ...m, providerName: p.name }))),
    ...companyModels.map((m) => ({ ...m, providerName: m.providerName })),
  ];
  const nameOf = (ref: string) => {
    const m = choosable.find((x) => x.id === ref);
    return m ? modelName(m) : null;
  };
  const companyRef = new Set(companyModels.map((m) => m.id));
  const modelCount = installation.reduce((n, p) => n + p.models.length, 0) + companyModels.length;
  // 16c.1 (KurumsalModelSohbet): "Sohbette" and who may use it; the Sohbet and Code tasks' model stays open to everyone
  const offer = { teams: view.data?.teams ?? [], lockedOpen: new Set([own.main, own.code].filter((ref): ref is string => Boolean(ref))) };
  const companyRows = companyModels.map((m) => ({ ...m, chatOffered: m.offered }));
  const offered = installation.reduce((n, p) => n + p.models.filter((m) => m.kind === 'chat' && m.chatOffered).length, 0) + companyRows.filter((m) => m.kind === 'chat' && m.chatOffered).length;
  const [added, setAdded] = useState<{ name: string; found: number | null; added: number | null } | null>(null);
  return (
    <>
      <div className="admin-info b2c-note" role="note" data-testid="models-offer-note">
        <Icons.info size={16} />
        <span>
          <strong>{t('models.offer.noteLead')}</strong> {t('models.offer.note')}
        </span>
      </div>
      {added ? (
        <Alert tone="success" title={added.found ? t('models.offer.added', { name: added.name, found: added.found, added: added.added ?? 0 }) : t('models.offer.addedNone', { name: added.name })} data-testid="provider-added" />
      ) : null}
      <ErrorAlert error={view.error ?? providers.error ?? models.error ?? routes.error} />
      <ProviderList providers={installation.length + company.length} models={modelCount} offered={offered}>
        {installation.map((p) => (
          <ProviderCard key={p.id} provider={p} models={p.models} endpoints={installationEndpoints(prefix, p.id)} onChanged={refresh} usage={usage} offer={offer} />
        ))}
        {company.map((p) => (
          <ProviderCard key={p.id} provider={p} models={companyRows.filter((m) => m.providerId === p.id)} endpoints={providerEndpoints(p.id)} onChanged={refresh} usage={usage} offer={{ lockedOpen: offer.lockedOpen }} />
        ))}
      </ProviderList>
      <TaskModels
        models={choosable}
        states={states}
        inherited={inheritedLabel(t, nameOf, null)}
        caption={t('models.routes.caption.installation')}
        testId="installation-routes"
        onSet={async (task, modelRef) => {
          // an older company provider's model is the company's choice; every other is the installation's
          if (modelRef && companyRef.has(modelRef)) await api('PUT', `/api/model-routes/${task}`, { scope: 'tenant', modelRef });
          else await api('PUT', `${prefix}/routes/${task}`, { modelRef });
          refresh();
        }}
      />
      {adding ? (
        <AddProviderDialog
          installation
          onClose={() => onAdding(false)}
          onTest={(draft) => api('POST', `${prefix}/providers/test`, { kind: draft.kind, baseUrl: draft.baseUrl ?? '', ...(draft.apiKey ? { apiKey: draft.apiKey } : {}), ...(draft.modelId ? { modelId: draft.modelId } : {}) })}
          onCreate={async (draft) => {
            const created = await api<{ name: string; found: number | null; added: number | null }>('POST', `${prefix}/providers`, installationDraft(draft));
            setAdded({ name: created.name, found: created.found, added: created.added });
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Bireysel: a company's Modeller (MM-001…MM-009): the platform's providers as they are ("Platform", read only),
 * the company's own ones to add, test and refresh, and the model per task, which overrides the platform's choice.
 */
function CompanyModels({ adding, onAdding }: { adding: boolean; onAdding: (open: boolean) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const providers = useProviders();
  const models = useModels();
  const routes = useRoutes();
  const refresh = () => {
    for (const key of [keys.providers, keys.models, keys.routes]) void queryClient.invalidateQueries({ queryKey: key });
  };
  const all = models.data?.models ?? [];
  const of = (providerId: string): ModelOption[] => all.filter((m) => m.providerId === providerId);
  const platform = (providers.data ?? []).filter((p) => p.scope === 'platform');
  const organization = (providers.data ?? []).filter((p) => p.scope === 'tenant');
  const own = routeMap((routes.data ?? []).filter((r) => r.scope === 'tenant'));
  const above = routeMap((routes.data ?? []).filter((r) => r.scope === 'platform'));
  const states = taskStates(own, [above]);
  const usage = modelUsage(states);
  const choosable = all.filter((m) => m.scope !== 'user');
  const nameOf = (ref: string) => {
    const m = all.find((x) => x.id === ref);
    return m ? modelName(m) : null;
  };
  const count = platform.length + organization.length;
  return (
    <>
      <ErrorAlert error={providers.error ?? models.error ?? routes.error} />
      <ProviderList providers={count} models={[...platform, ...organization].reduce((n, p) => n + of(p.id).length, 0)}>
        {platform.map((p) => (
          <ProviderCard key={p.id} provider={p} models={of(p.id)} endpoints={null} onChanged={refresh} usage={usage} scopeLabel={t('models.scopes.platform')} />
        ))}
        {organization.map((p) => (
          <ProviderCard key={p.id} provider={p} models={of(p.id)} endpoints={providerEndpoints(p.id)} onChanged={refresh} usage={usage} />
        ))}
      </ProviderList>
      <TaskModels
        models={choosable}
        states={states}
        inherited={inheritedLabel(t, nameOf, 'platform')}
        caption={t('models.routes.caption.tenant')}
        testId="org-routes"
        onSet={async (task, modelRef) => {
          await (modelRef ? api('PUT', `/api/model-routes/${task}`, { scope: 'tenant', modelRef }) : api('DELETE', `/api/model-routes/${task}?scope=tenant`));
          refresh();
        }}
      />
      {adding ? (
        <AddProviderDialog
          installation={false}
          onClose={() => onAdding(false)}
          onTest={(draft) => api('POST', '/api/providers/test', ownDraftTest('tenant', draft))}
          onCreate={async (draft) => {
            await api('POST', '/api/providers', ownDraft('tenant', draft));
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

export function ModelsAdminPage() {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const { tab } = useParams();
  const [adding, setAdding] = useState(false);
  const [day, setDay] = useState<CapacityDay>('today');
  // kurumsal: "Sağlayıcılar ve görevler" and "Kapasite" (D-135)
  const capacity = capabilities.singleCompany && tab === 'capacity';
  return (
    <AdminLayout
      section="models"
      title={t('admin.sections.models')}
      actions={
        capacity ? (
          <CapacityDaySelect value={day} onChange={setDay} />
        ) : (
          <Button icon={<Icons.plus size={16} />} onClick={() => setAdding(true)} data-testid="provider-add-open">
            {t('models.add.title')}
          </Button>
        )
      }
    >
      {capabilities.singleCompany ? (
        <SubTabs
          label={t('models.tabsLabel')}
          items={[
            { to: '/admin/models', end: true, label: t('models.tabs.providers'), testId: 'models-tab-providers' },
            { to: '/admin/models/capacity', label: t('models.tabs.capacity'), testId: 'models-tab-capacity' },
          ]}
        />
      ) : null}
      {capacity ? (
        <CapacityPanel endpoint="/api/admin/models/capacity" day={day} />
      ) : capabilities.singleCompany ? (
        <InstallationModels adding={adding} onAdding={setAdding} />
      ) : (
        <CompanyModels adding={adding} onAdding={setAdding} />
      )}
    </AdminLayout>
  );
}
